import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import {
  ActivityType,
  ActivityFilterParams,
  ActivityListResponse,
  ActivityListItem,
  AssetSearchResponse,
  CanDeleteResponse,
  ChecklistTemplate,
  CostTemplate,
  Schedule,
  ScheduleListResponse,
  ScheduleFilterParams,
  UserGroup
} from '../../types/schedule.types';

export const scheduleApi = createApi({
  reducerPath: 'scheduleApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Schedule', 'ActivityType', 'Asset', 'Activity'],
  endpoints: (builder) => ({
    // Get all activities with filtering, sorting, and pagination
    getActivities: builder.query<{ totalCount: number; results: ActivityListItem[] }, ActivityFilterParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        const {
          page,
          page_size,
          limit,
          sort_by,
          sort_order,
          search,
          status,
          user_group,
          assigned_user,
          asset,
          location,
          start_date,
          end_date,
          ...rest
        } = params as ActivityFilterParams & Record<string, unknown>;

        const effectivePageSize = page_size ?? limit;

        if (search != null && search !== '') queryParams.append('search', String(search));
        if (status != null && status !== '') queryParams.append('status', String(status));
        if (user_group != null && user_group !== '') queryParams.append('user_group', String(user_group));
        if (assigned_user != null && assigned_user !== '') queryParams.append('assigned_user', String(assigned_user));
        if (asset != null && asset !== '') queryParams.append('asset', String(asset));
        if (location != null && location !== '') queryParams.append('location', String(location));
        if (start_date != null && start_date !== '') queryParams.append('start_date', String(start_date));
        if (end_date != null && end_date !== '') queryParams.append('end_date', String(end_date));
        if (page != null) queryParams.append('page', String(page));
        if (effectivePageSize != null) queryParams.append('page_size', String(effectivePageSize));
        if (sort_by != null && sort_by !== '') queryParams.append('sort_by', String(sort_by));
        if (sort_order != null) queryParams.append('sort_order', String(sort_order));
        Object.entries(rest).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
          }
        });

        const queryString = queryParams.toString();
        return `schedule/activities/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: ActivityListResponse) => ({
        totalCount: response.count ?? 0,
        results: response.results || [],
      }),
      providesTags: ['Activity'],
    }),

    // Get all schedules with filtering, sorting, and pagination
    getSchedules: builder.query<ScheduleListResponse, ScheduleFilterParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        const {
          page,
          page_size,
          sort_by,
          sort_order,
          search,
          name,
          description,
          asset_name,
          asset_tag,
          user_group_name,
          activity_type_id,
          location_id,
          status,
          start_date_from,
          start_date_to,
          end_date_from,
          end_date_to,
          ...rest
        } = params as ScheduleFilterParams & Record<string, unknown>;

        if (search != null && search !== '') queryParams.append('search', String(search));
        if (name != null && name !== '') queryParams.append('name', String(name));
        if (description != null && description !== '') queryParams.append('description', String(description));
        if (asset_name != null && asset_name !== '') queryParams.append('asset_name', String(asset_name));
        if (asset_tag != null && asset_tag !== '') queryParams.append('asset_tag', String(asset_tag));
        if (user_group_name != null && user_group_name !== '') queryParams.append('user_group_name', String(user_group_name));
        if (activity_type_id != null && activity_type_id !== '') queryParams.append('activity_type', String(activity_type_id));
        if (location_id != null && location_id !== '') queryParams.append('location_id', String(location_id));
        if (status !== undefined && status !== null && status !== '') queryParams.append('status', String(status));
        if (start_date_from != null && start_date_from !== '') queryParams.append('start_date_from', String(start_date_from));
        if (start_date_to != null && start_date_to !== '') queryParams.append('start_date_to', String(start_date_to));
        if (end_date_from != null && end_date_from !== '') queryParams.append('end_date_from', String(end_date_from));
        if (end_date_to != null && end_date_to !== '') queryParams.append('end_date_to', String(end_date_to));
        if (page != null) queryParams.append('page', String(page));
        if (page_size != null) queryParams.append('limit', String(page_size));
        if (sort_by != null && sort_by !== '') {
          const ordering = sort_order === 'desc' ? `-${sort_by}` : sort_by;
          queryParams.append('ordering', ordering);
        }
        Object.entries(rest).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
          }
        });

        const queryString = queryParams.toString();
        return `schedule/schedules/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (
        response: any,
        _meta: unknown,
        arg: ScheduleFilterParams
      ): ScheduleListResponse => {
        // Use the requested page/page_size from the query so the returned response
        // reflects the current page (response.next would give the next page, and
        // when next is null we would wrongly report page 1).
        const requestedPage = arg?.page != null ? Number(arg.page) : 1;
        const requestedPageSize =
          arg?.page_size != null ? Number(arg.page_size) : response.limit ?? 10;
        return {
          totalCount: response.count ?? 0,
          page: requestedPage,
          pageSize: requestedPageSize,
          schedules: response.results || []
        };
      },
      providesTags: ['Schedule'],
    }),

    // Get a single schedule by ID
    getScheduleById: builder.query<Schedule, string>({
      query: (id) => `schedule/schedules/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Schedule', id }],
    }),

    // Create a new schedule - Always use FormData like asset API
    createSchedule: builder.mutation<Schedule, FormData>({
      query: (formData) => ({
        url: 'schedule/schedules/',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['Schedule'],
    }),

    // Update an existing schedule - Always use FormData like asset API
    updateSchedule: builder.mutation<Schedule, { id: string; scheduleData: FormData }>({
      query: ({ id, scheduleData }) => ({
        url: `schedule/schedules/${id}/`,
        method: 'PUT',
        body: scheduleData,
        formData: true,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Schedule', id },
        'Schedule',
      ],
    }),

    // Delete a schedule
    deleteSchedule: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `schedule/schedules/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Schedule'],
    }),

    // Check if a schedule can be deleted
    canDeleteSchedule: builder.query<CanDeleteResponse, string>({
      query: (id) => `schedule/schedules/${id}/can_delete/`,
    }),

    // Get all activity types
    getActivityTypes: builder.query<ActivityType[], Record<string, any>>({
      query: (params = {}) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });

        return {
          url: "master/schedule/activity-types/",
          method: "GET",
          params: queryParams,
        };
      },
      transformResponse: (response: any) => {
        // Check if response is in the expected format
        if (response && response.data) {
          return { results: response.data };
        }
        // If response is already an array, wrap it in results
        if (Array.isArray(response)) {
          return { results: response };
        }
        // Return the original response if it already has results
        return response;
      },
      providesTags: ['ActivityType'],
    }),

    // New dropdown-specific endpoint for activity types
    getActivityTypesDropdown: builder.query<ActivityType[],{
      page?: number;
      limit?: number;
      search?: string;
      dropdown?: boolean;
      } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params?.dropdown) {
          queryParams.append('dropdown', 'true');
        }
        if (params?.page) {
          queryParams.append('page', String(params.page));
        }
        if (params?.limit) {
          queryParams.append('limit', String(params.limit));
        }
        if (params?.search) {
          queryParams.append('search', params.search);
        }

        const queryString = queryParams.toString();

        return `master/schedule/activity-types/dropdown/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: { results: ActivityType[] }) => response.results,
      providesTags: ['ActivityType'],
    }),
    // Get all assets
    getAssets: builder.query<AssetSearchResponse, void>({
      query: () => 'assets/',
      providesTags: ['Asset'],
    }),
    
    // Search assets
    searchAssets: builder.query<
      AssetSearchResponse,
      { query: string; page?: number; limit?: number; dropdown?: boolean }
    >({
      query: ({ query, page = 1, limit = 25, dropdown }) => {
        const queryParams = new URLSearchParams({
          search: query,
          page: String(page),
          limit: String(limit),
        });
        if (dropdown) {
          queryParams.append('dropdown', 'true');
        }
        return `schedule/assets/search/?${queryParams.toString()}`;
      },
    }),

    // Get user groups
    getUserGroups: builder.query<
      { results: UserGroup[] } | UserGroup[],
      Record<string, any>
    >({
      query: (params = {}) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });

        const baseUrl = `master/schedule/user-groups/`;

        return {
          url: baseUrl,
          method: "GET",
          params: queryParams,
        };
      },
      transformResponse: (response: any) => {
        // Check if response is in the expected format
        if (response && response.data) {
          return { results: response.data };
        }
        // If response is already an array, wrap it in results
        if (Array.isArray(response)) {
          return { results: response };
        }
        // Return the original response if it already has results
        return response;
      },
    }),

    // New dropdown-specific endpoint for user groups
    getUserGroupsDropdown: builder.query<
      { results: UserGroup[] } | UserGroup[],
      {
        page?: number;
        limit?: number;
        search?: string;
        dropdown?: boolean;
      } | void
    >({
      query: (params) => {
        const companyId = localStorage.getItem('currentCompanyId');
        const queryParams = new URLSearchParams();

        if (params?.dropdown) {
          queryParams.append('dropdown', 'true');
        }
        if (params?.page) {
          queryParams.append('page', String(params.page));
        }
        if (params?.limit) {
          queryParams.append('limit', String(params.limit));
        }
        if (params?.search) {
          queryParams.append('search', params.search);
        }

        const queryString = queryParams.toString();

        if (companyId) {
          return `master/schedule/${companyId}/user-groups/dropdown/${queryString ? `?${queryString}` : ''}`;
        }

        return `master/schedule/user-groups/dropdown/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: any) => {
        if (response && response.data) {
          return { results: response.data };
        }
        if (Array.isArray(response)) {
          return { results: response };
        }
        return response;
      },
    }),
  
    // Get cost templates
    getCostTemplates: builder.query<
      { results: CostTemplate[] } | CostTemplate[],
      Record<string, any>
    >({
      query: (params = {}) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });

        const baseUrl = `master/schedule/cost-templates/`;

        return {
          url: baseUrl,
          method: "GET",
          params: queryParams,
        };
      },
      transformResponse: (response: any) => {
        // Check if response is in the expected format
        if (response && response.data) {
          return { results: response.data };
        }
        // If response is already an array, wrap it in results
        if (Array.isArray(response)) {
          return { results: response };
        }
        // Return the original response if it already has results
        return response;
      },
    }),

    // New dropdown-specific endpoint for cost templates
    getCostTemplatesDropdown: builder.query<
    { results: CostTemplate[] } | CostTemplate[],
      {
        page?: number;
        limit?: number;
        search?: string;
        dropdown?: boolean;
      } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params?.dropdown) {
          queryParams.append('dropdown', 'true');
        }
        if (params?.page) {
          queryParams.append('page', String(params.page));
        }
        if (params?.limit) {
          queryParams.append('limit', String(params.limit));
        }
        if (params?.search) {
          queryParams.append('search', params.search);
        }

        const queryString = queryParams.toString();

        return `master/schedule/cost-templates/dropdown/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: any) => {
        if (response && response.results) {
          return { results: response.results };
        }
        if (Array.isArray(response)) {
          return { results: response };
        }
        return response;
      },
    }),

    // Get checklist templates
    getChecklistTemplates: builder.query<
      { results: ChecklistTemplate[] } | ChecklistTemplate[],
      Record<string, any>
    >({
      query: (params = {}) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });

        const baseUrl = `master/schedule/checklist-templates/`;

        return {
          url: baseUrl,
          method: "GET",
          params: queryParams,
        };
      },
      transformResponse: (response: any) => {
        // Check if response is in the expected format
        if (response && response.data) {
          return { results: response.data };
        }
        // If response is already an array, wrap it in results
        if (Array.isArray(response)) {
          return { results: response };
        }
        // Return the original response if it already has results
        return response;
      },
    }),

    // New dropdown-specific endpoint for checklist templates
    getChecklistTemplatesDropdown: builder.query<
      { results: ChecklistTemplate[] } | ChecklistTemplate[],
      {
        page?: number;
        limit?: number;
        search?: string;
        dropdown?: boolean;
      } | void
    >({
      query: (params) => {
        const companyId = localStorage.getItem('currentCompanyId');
        const queryParams = new URLSearchParams();

        if (params?.dropdown) {
          queryParams.append('dropdown', 'true');
        }
        if (params?.page) {
          queryParams.append('page', String(params.page));
        }
        if (params?.limit) {
          queryParams.append('limit', String(params.limit));
        }
        if (params?.search) {
          queryParams.append('search', params.search);
        }

        const queryString = queryParams.toString();

        if (companyId) {
          return `master/schedule/${companyId}/checklist-templates/dropdown/${queryString ? `?${queryString}` : ''}`;
        }

        return `master/schedule/checklist-templates/dropdown/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: any) => {
        if (response && response.results) {
          return { results: response.results };
        }
        if (Array.isArray(response)) {
          return { results: response };
        }
        return response;
      },
    }),

    // New dropdown-specific endpoint for schedules
    getSchedulesDropdown: builder.query<ScheduleListResponse, ScheduleFilterParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        const {
          page,
          limit,
          sort_by,
          sort_order,
          search,
          activity_type_id,
          location_id,
          status,
          start_date_from,
          start_date_to,
          end_date_from,
          end_date_to,
          ...rest
        } = params as ScheduleFilterParams & Record<string, unknown>;

        if (search != null && search !== '') queryParams.append('search', String(search));
        if (activity_type_id != null && activity_type_id !== '') queryParams.append('activity_type', String(activity_type_id));
        if (location_id != null && location_id !== '') queryParams.append('location_id', String(location_id));
        if (status !== undefined && status !== null && status !== '') queryParams.append('status', String(status));
        if (start_date_from != null && start_date_from !== '') queryParams.append('start_date_from', String(start_date_from));
        if (start_date_to != null && start_date_to !== '') queryParams.append('start_date_to', String(start_date_to));
        if (end_date_from != null && end_date_from !== '') queryParams.append('end_date_from', String(end_date_from));
        if (end_date_to != null && end_date_to !== '') queryParams.append('end_date_to', String(end_date_to));
        if (page != null) queryParams.append('page', String(page));
        if (limit != null) queryParams.append('limit', String(limit));
        if (sort_by != null && sort_by !== '') {
          const ordering = sort_order === 'desc' ? `-${sort_by}` : sort_by;
          queryParams.append('ordering', ordering);
        }
        Object.entries(rest).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
          }
        });

        const queryString = queryParams.toString();
        return `schedule/schedules/dropdown/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (
        response: any,
        _meta: unknown,
        arg: ScheduleFilterParams
      ): ScheduleListResponse => {
        // Use the requested page/page_size from the query so the returned response
        // reflects the current page (response.next would give the next page, and
        // when next is null we would wrongly report page 1).
        const requestedPage = arg?.page != null ? Number(arg.page) : 1;
        const requestedPageSize =
          arg?.limit != null ? Number(arg.limit) : response.limit ?? 10;
        return {
          totalCount: response.count ?? 0,
          page: requestedPage,
          pageSize: requestedPageSize,
          schedules: response.results || []
        };
      },
      providesTags: ['Schedule'],
    }),
    
    // Upload attachment to a schedule
    uploadAttachment: builder.mutation<any, { scheduleId: string; file: File }>({
      query: ({ scheduleId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        
        return {
          url: `schedule/schedules/${scheduleId}/upload_attachment/`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'Schedule', id: scheduleId },
      ],
    }),
    
    // Delete attachment from a schedule
    deleteAttachment: builder.mutation<void, { scheduleId: string; attachmentId: string }>({
      query: ({ scheduleId, attachmentId }) => ({
        url: `schedule/schedules/${scheduleId}/delete_attachment/?attachment_id=${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'Schedule', id: scheduleId },
      ],
    }),
  }),
});

export const {
  useGetActivitiesQuery,
  useGetSchedulesQuery,
  useGetSchedulesDropdownQuery,
  useGetScheduleByIdQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
  useDeleteScheduleMutation,
  useCanDeleteScheduleQuery,
  useGetActivityTypesQuery,
  useGetActivityTypesDropdownQuery,
  useGetAssetsQuery,
  useSearchAssetsQuery,
  useGetUserGroupsQuery,
  useGetUserGroupsDropdownQuery,
  useGetCostTemplatesQuery,
  useGetCostTemplatesDropdownQuery,
  useGetChecklistTemplatesQuery,
  useGetChecklistTemplatesDropdownQuery,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
} = scheduleApi;

// Add alias for backward compatibility
export const useListUserGroupsQuery = useGetUserGroupsQuery;
